/**
 * @file LoginPage.js
 * @description Componente de página para el inicio de sesión de usuarios.
 * Permite a los usuarios autenticarse en la aplicación utilizando su correo electrónico y contraseña.
 * Muestra mensajes de éxito o error según el resultado del intento de inicio de sesión.
 */
import React, { useContext, useState } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import PasswordInput from '../components/PasswordInput';

const LoginPage = () => {
    const { loginUser } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState(location.state?.message);
    const [rememberMe, setRememberMe] = useState(false);
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        try {
            const response = await loginUser(email, password, rememberMe);

            // Decode token to check user role
            const decodedUser = response.data ? JSON.parse(atob(response.data.token.split('.')[1])) : null;

            // Redirect based on user role
            // Superadmin always goes to admin dashboard, regardless of business_id association
            if (decodedUser && decodedUser.role === 'superadmin') {
                navigate('/admin/dashboard');
            } else {
                // Regular users go to sales page (accessible to all roles). Tarea 027c: login
                // ya no rechaza pending_verification — el banner de "pendiente de verificar"
                // vive dentro de la app (Layout), no acá.
                navigate('/sales');
            }
        } catch (error) {
            console.error('Failed to login', error);
            if (error.response?.status === 429) {
                // Rate limiting (429 Too Many Requests)
                setError(t('loginPage.tooManyAttempts'));
            } else if (!error.response) {
                // Sin respuesta del servidor: red caída, CORS, timeout, backend no disponible.
                // No es lo mismo que credenciales inválidas (la petición ni llegó al backend).
                setError(t('loginPage.networkError'));
            } else {
                setError(t('loginPage.invalidCredentials'));
            }
        }
    };

    return (
        <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
            <Row>
                <Col>
                    <Card style={{ width: '24rem' }}>
                        <Card.Body>
                            <div className="text-center mb-4">
                                <img src="/logo_evazard_transparente.svg" alt="Evazard Logo" className="evazard-logo" style={{ width: '320px', height: '320px' }} />
                            </div>
                            <Card.Title className="text-center mb-4">{t('loginPage.title')}</Card.Title>
                            {successMessage && (
                                <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
                                    {successMessage}
                                </Alert>
                            )}
                            {error && <Alert variant="danger">{error}</Alert>}
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="email">
                                    <Form.Label>{t('loginPage.emailLabel')}</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('loginPage.emailPlaceholder')}
                                        required
                                    />
                                </Form.Group>

                                <PasswordInput
                                    controlId="password"
                                    label={t('loginPage.passwordLabel')}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={t('loginPage.passwordPlaceholder')}
                                    required
                                />

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label={t('loginPage.rememberMe')}
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    <Link to="/forgot-password">{t('loginPage.forgotPassword')}</Link>
                                </div>

                                <div className="d-grid">
                                    <Button variant="primary" type="submit">
                                        {t('loginPage.loginButton')}
                                    </Button>
                                </div>
                            </Form>
                            <div className="text-center mt-3">
                                <Link to="/register">{t('loginPage.registerPrompt')}</Link>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default LoginPage;